import { FC, HTMLAttributes, JSX, ReactNode, useEffect, useRef, useState } from "react"
import "intersection-observer"

function isElementInViewport(el) {
    if (!el) {
        return false
    }

    const rect = el.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const windowWidth = window.innerWidth || document.documentElement.clientWidth

    // Check if the element is at least partially vertically in view
    const verticallyInView = rect.top < windowHeight && rect.bottom > 0

    // Check if the element is at least partially horizontally in view
    const horizontallyInView = rect.left < windowWidth && rect.right > 0

    return verticallyInView && horizontallyInView
}
type IntersectionProps = {
    once?: boolean
    threshold?: number
    onChange?: (isIntersecting: boolean) => void
    className?: string
    render?: (props: { active: boolean }) => JSX.Element
    children?: ReactNode
    rootMargin?: string
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">

export const Intersection: FC<IntersectionProps> = ({ once, threshold = 0.15, rootMargin = "0px 0px 0px 0px", onChange, className, render: Render, children, ...divProps }) => {
    const [isIntersecting, setIsIntersecting] = useState(false)
    const ref = useRef<HTMLDivElement | null>(null)
    const intRef = useRef(isIntersecting)
    intRef.current = isIntersecting

    useEffect(() => {
        const scrollHandler = () => {
            if (ref.current && isElementInViewport(ref.current)) {
                onChange?.(intRef.current)
            }
        }
        addEventListener("scroll", scrollHandler)
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting !== intRef.current) {
                        setIsIntersecting(entry.isIntersecting)
                        intRef.current = entry.isIntersecting
                        if (onChange) {
                            onChange(entry.isIntersecting)
                        }
                        if (once && entry.isIntersecting) {
                            observer.disconnect()
                            removeEventListener("scroll", scrollHandler)
                        }
                    }
                })
            },
            { rootMargin, threshold }
        )

        if (ref.current) {
            observer.observe(ref.current)
        }

        return () => {
            observer.disconnect()
            removeEventListener("scroll", scrollHandler)
        }
    }, [threshold, onChange, once])

    const child = Render ? <Render active={isIntersecting} /> : children

    return (
        <div {...divProps} className={className} ref={ref} data-active={isIntersecting || undefined}>
            {child}
        </div>
    )
}
